import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppFrameWide - THE CONTACT FRAME IS TOLD WHEN IT SITS IN THE WIDE (THREE-PANE) LAYOUT (business look wave B, 2026-10-13).
//
// Why: the frame's own sheets decide "this is a phone" from the FRAME's width (`@media(max-width:620px)`), and in three
// panes at 1100-1400px the centre pane - so the frame - is NARROWER than that on a desktop with a mouse. The phone rules
// then applied there: 44px touch floors on every bubble and control, and the page header's "Insights" button drawn as a
// tall box (seen in the wave A pictures at 1280). A frame cannot see the window it is embedded in, so the app says it:
// `data-wac-wide="1"` on the frame's `<html>` while the window is at or above `AppLayout.WideMin`; every phone rule of
// the frame sheets carries `Not` (`AppFrameA11y.TouchCss`, `AppReactions.Css`, `AppTokens.FrameFlat`).
// ⚠ A coarse pointer keeps its 44px bubble floor whatever the width (`AppReactions.Css`, `@media(pointer:coarse)`).
// ⛔ A string edit at build time (`Mark`, like `AppAssistRules.Embedded`) and one attribute from outside (`Apply`) - no
// inline style, no script in the frame.

/** The little of the frame's root element `Apply` touches. */
export interface IFrameWideRoot {
    setAttribute(Name: string, Value: string): void;
    removeAttribute(Name: string): void;
}

export class AppFrameWide {
    public static readonly Attr: string = "data-wac-wide";
    /** Appended to the frame sheets' root selector inside a phone rule: "...only when the window is not wide". */
    public static readonly Not: string = ":not([data-wac-wide])";

    /**
     * The string edit `Mark` and `AppAssistRules.Embedded` share: ` Attr="1"` inserted after the first `<html`
     * tag of the normalized text, or the text unchanged when there is no such tag (each caller passes its own
     * attribute name and its own diagnostic label).
     */
    public static MarkAttr = (Text: string, Attr: string, Label: string): string => {
        if (getCoreUtils().KErrorMode === true) { return Text; }
        try {
            const Found: RegExpExecArray | null = /<html(\s|>)/i.exec(Text);
            if (Found === null) { return Text; }
            const At: number = Found.index + 5;
            return Text.substring(0, At) + " " + Attr + "=\"1\"" + Text.substring(At);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, Label);
            return Text;
        }
    };

    /** The page's `<html>` marked (or left alone) before the frame is built. No `<html>` tag = the text unchanged. */
    public static Mark = (Html: string, Wide: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return Html; }
        try {
            const Text: string = nz(Html);
            if (Wide !== true) { return Text; }
            return AppFrameWide.MarkAttr(Text, AppFrameWide.Attr, "AppFrameWide.Mark");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameWide.Mark");
            return Html;
        }
    };

    /** The window crossed the breakpoint with a chat open: the frame already built follows. */
    public static Apply = (Root: IFrameWideRoot | null, Wide: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Root === null) { return; }
            if (Wide === true) { Root.setAttribute(AppFrameWide.Attr, "1"); return; }
            Root.removeAttribute(AppFrameWide.Attr);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppFrameWide.Apply");
        }
    };
}
